import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Trash2, Plus, Pencil, Save, X } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Header } from "./WorkspacesTab";
import { useConfirm } from "@/contexts/ConfirmContext";

const ROLE_TABS = [
  { key: "viewer", label: "Viewers" },
  { key: "creator", label: "Creators" },
  { key: "manager", label: "Managers" },
];

export default function GlobalSupportTab() {
  const [support, setSupport] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [faq, setFaq] = useState([]);
  const inputRef = useRef();

  const refresh = async () => {
    const [cfg, items] = await Promise.all([
      api.get("/super-admin/config"),
      api.get("/super-admin/faq"),
    ]);
    setSupport(cfg.data.support);
    setFaq(items.data);
  };

  useEffect(() => { refresh().catch(() => toast.error("Kon support-config niet laden")); }, []);

  const patch = (p) => { setSupport((s) => ({ ...s, ...p })); setDirty(true); };

  const uploadAvatar = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/super-admin/upload-avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      patch({ avatar_file_id: data.file_id });
    } catch { toast.error("Uploaden mislukt"); }
  };

  const saveSupport = async () => {
    setSaving(true);
    try {
      await api.patch("/super-admin/config", { support });
      toast.success("Opgeslagen");
      setDirty(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
    finally { setSaving(false); }
  };

  if (!support) return <div className="text-sm text-gray-500">Laden…</div>;

  return (
    <div data-testid="sa-support">
      <Header title="Global Support & FAQ" sub="1-op-1 wat elke tenant op zijn Support-pagina ziet. FAQ wordt gefilterd op rol." />

      <section className="rounded-[var(--global-radius-xl)] border border-gray-200 bg-white p-6 mb-8">
        <div className="text-[11px] uppercase tracking-widest text-gray-400 mb-4">Contactpersoon</div>
        <div className="grid grid-cols-[120px_1fr] gap-6 items-start">
          <div>
            <input ref={inputRef} type="file" hidden accept="image/*" onChange={(e) => uploadAvatar(e.target.files?.[0])} />
            <button
              onClick={() => inputRef.current?.click()}
              data-testid="sa-support-avatar"
              className="w-24 h-24 rounded-full overflow-hidden bg-[#f3f3f1] border border-gray-200 flex items-center justify-center hover:border-gray-400 transition-colors"
            >
              {support.avatar_file_id
                ? <img src={fileUrl(support.avatar_file_id)} alt="avatar" className="w-full h-full object-cover" />
                : <UploadCloud className="w-5 h-5 text-gray-400" strokeWidth={1.75} />}
            </button>
          </div>
          <div className="space-y-3">
            <Field label="Naam" value={support.name} onChange={(v) => patch({ name: v })} testId="sa-support-name" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Telefoon" value={support.phone} onChange={(v) => patch({ phone: v })} testId="sa-support-phone" />
              <Field label="E-mail" value={support.email} onChange={(v) => patch({ email: v })} testId="sa-support-email" type="email" />
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <Toggle label="Toon telefoonnummer" checked={support.show_phone} onChange={(v) => patch({ show_phone: v })} testId="sa-support-show-phone" />
              <Toggle label="Toon e-mailadres" checked={support.show_email} onChange={(v) => patch({ show_email: v })} testId="sa-support-show-email" />
            </div>
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <Button
            onClick={saveSupport}
            disabled={!dirty || saving}
            data-testid="sa-support-save"
            className="h-10 px-6 rounded-[var(--global-radius)] bg-[var(--global-action)] text-white hover:opacity-90 disabled:opacity-50"
          >{saving ? "Opslaan…" : "Opslaan"}</Button>
        </div>
      </section>

      <FaqManager faq={faq} onChange={refresh} />
    </div>
  );
}

function Field({ label, value, onChange, testId, type = "text" }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-widest text-gray-400 mb-1">{label}</div>
      <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="bg-white" data-testid={testId} />
    </label>
  );
}

function Toggle({ label, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between h-11 px-3 rounded-[var(--global-radius)] border border-gray-200 bg-white">
      <div className="text-sm text-[#1a1a1a]">{label}</div>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}

function FaqManager({ faq, onChange }) {
  const [activeRole, setActiveRole] = useState("viewer");
  const [editing, setEditing] = useState(null); // {id?, question, answer, role}
  const [busy, setBusy] = useState(false);

  const items = faq.filter((f) => (f.role || "viewer") === activeRole);
  const roleCount = (r) => faq.filter((f) => (f.role || "viewer") === r).length;

  const startNew = () => setEditing({ question: "", answer: "", role: activeRole });
  const startEdit = (f) => setEditing({ id: f.id, question: f.question, answer: f.answer, role: f.role || "viewer" });

  const save = async () => {
    if (!editing?.question?.trim() || !editing?.answer?.trim()) return;
    setBusy(true);
    try {
      const payload = { question: editing.question, answer: editing.answer, role: editing.role };
      if (editing.id) {
        await api.patch(`/super-admin/faq/${editing.id}`, payload);
      } else {
        await api.post("/super-admin/faq", payload);
      }
      toast.success("Opgeslagen");
      setEditing(null);
      await onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    const ok = await confirm({
      title: "Deze vraag verwijderen?",
      body: "De FAQ-vraag wordt direct uit alle hubs verwijderd.",
      confirmLabel: "Verwijderen",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/super-admin/faq/${id}`);
      await onChange();
    } catch { toast.error("Verwijderen mislukt"); }
  };

  return (
    <section className="rounded-[var(--global-radius-xl)] border border-gray-200 bg-white p-6" data-testid="sa-faq">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1 bg-[#f3f3f1] rounded-[var(--global-radius)] p-1">
          {ROLE_TABS.map((r) => {
            const active = r.key === activeRole;
            return (
              <button
                key={r.key}
                onClick={() => { setActiveRole(r.key); setEditing(null); }}
                data-testid={`sa-faq-role-${r.key}`}
                className={`inline-flex items-center gap-2 h-8 px-3 rounded-[calc(var(--global-radius)-2px)] text-xs font-medium transition-colors ${
                  active ? "bg-white text-[#1a1a1a] shadow-sm" : "text-gray-600 hover:text-[#1a1a1a]"
                }`}
              >
                {r.label}
                <span className={`text-[10px] font-mono px-1.5 h-4 inline-flex items-center rounded-sm ${active ? "bg-gray-100 text-gray-600" : "bg-white/60 text-gray-500"}`}>{roleCount(r.key)}</span>
              </button>
            );
          })}
        </div>
        {!editing && (
          <button
            onClick={startNew}
            data-testid="sa-faq-add"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-[var(--global-radius)] bg-[var(--global-action)] text-white text-xs font-medium hover:opacity-90"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={2} /> Nieuwe vraag
          </button>
        )}
      </div>

      {editing && (
        <div className="mb-5 rounded-[var(--global-radius-lg)] border border-gray-200 bg-[#fafafa] p-4 space-y-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-gray-400 mb-1">Zichtbaar voor</div>
            <div className="flex gap-1">
              {ROLE_TABS.map((r) => (
                <button
                  key={r.key}
                  onClick={() => setEditing({ ...editing, role: r.key })}
                  data-testid={`sa-faq-edit-role-${r.key}`}
                  className={`h-8 px-3 rounded-[var(--global-radius)] text-xs font-medium border transition-colors ${
                    editing.role === r.key
                      ? "bg-[var(--global-action)] text-white border-transparent"
                      : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"
                  }`}
                >{r.label}</button>
              ))}
            </div>
          </div>
          <Input
            value={editing.question}
            onChange={(e) => setEditing({ ...editing, question: e.target.value })}
            placeholder="Vraag"
            className="bg-white"
            data-testid="sa-faq-edit-question"
          />
          <textarea
            value={editing.answer}
            onChange={(e) => setEditing({ ...editing, answer: e.target.value })}
            rows={5}
            placeholder="Antwoord (HTML toegestaan)"
            className="w-full bg-white border border-gray-200 rounded-[var(--global-radius)] p-3 text-sm"
            data-testid="sa-faq-edit-answer"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditing(null)} className="h-9 rounded-[var(--global-radius)]">
              <X className="w-3.5 h-3.5 mr-1.5" /> Annuleer
            </Button>
            <Button onClick={save} disabled={busy} data-testid="sa-faq-save" className="h-9 rounded-[var(--global-radius)] bg-[var(--global-action)] text-white hover:opacity-90">
              <Save className="w-3.5 h-3.5 mr-1.5" /> Opslaan
            </Button>
          </div>
        </div>
      )}

      <div className="divide-y divide-gray-100 border-y border-gray-100">
        {items.length === 0 && !editing ? (
          <div className="py-8 text-center text-sm text-gray-500">Nog geen FAQ-items voor {ROLE_TABS.find((r) => r.key === activeRole)?.label.toLowerCase()}.</div>
        ) : (
          items.map((f) => (
            <div key={f.id} className="py-3 flex items-start gap-3" data-testid={`sa-faq-row-${f.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#1a1a1a]">{f.question}</div>
                <div className="mt-1 text-xs text-gray-500 line-clamp-2" dangerouslySetInnerHTML={{ __html: f.answer }} />
              </div>
              <button onClick={() => startEdit(f)} className="w-7 h-7 rounded-md text-gray-500 hover:text-[#1a1a1a] hover:bg-gray-100 flex items-center justify-center" data-testid={`sa-faq-edit-${f.id}`}>
                <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
              </button>
              <button onClick={() => del(f.id)} className="w-7 h-7 rounded-md text-gray-500 hover:text-red-600 hover:bg-gray-100 flex items-center justify-center" data-testid={`sa-faq-delete-${f.id}`}>
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
              </button>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
